import { colors } from "@/src/utils/colors"
import { logger } from "@/src/utils/logger"
import process from "process"

/**
 * CLI UI utilities for consistent visual styling
 */

/**
 * Get the current terminal width with a fallback
 */
export function getTerminalWidth(): number {
  return process.stdout.columns || 80
}

/**
 * Creates a bordered box with content
 */
export function createBox(
  title: string,
  content: string[],
  options?: {
    borderColor?: "cyan" | "yellow" | "red" | "green" | "gray"
    padding?: boolean
  }
) {
  const borderColor = options?.borderColor || "gray"
  const padding = options?.padding !== false

  // Box drawing characters
  const topLeft = ""
  const topRight = ""
  const bottomLeft = ""
  const bottomRight = ""
  const horizontal = "-"
  const vertical = ""

  // Use terminal width with max constraint for readability
  const terminalWidth = getTerminalWidth()
  const boxWidth = Math.min(terminalWidth - 4, 80) // Max 80 chars for readability

  const colorFn = colors[borderColor]
  const topBorder = colorFn(
    topLeft + horizontal.repeat(boxWidth - 2) + topRight
  )
  const bottomBorder = colorFn(
    bottomLeft + horizontal.repeat(boxWidth - 2) + bottomRight
  )
  const sideBorder = colorFn(vertical)

  // Create the box
  const lines = []

  if (padding) lines.push("")

  lines.push(topBorder)

  // Empty line before title
  lines.push(`${sideBorder}${" ".repeat(boxWidth - 2)}${sideBorder}`)

  // Title line
  const titlePadding = Math.floor((boxWidth - 4 - stripAnsi(title).length) / 2)
  const titleLine = title
    .padStart(titlePadding + stripAnsi(title).length)
    .padEnd(boxWidth - 4)
  lines.push(`${sideBorder} ${titleLine} ${sideBorder}`)

  // Empty line after title
  lines.push(`${sideBorder}${" ".repeat(boxWidth - 2)}${sideBorder}`)

  // Content lines
  content.forEach((line) => {
    const strippedLength = stripAnsi(line).length
    const padding = boxWidth - 4 - strippedLength
    const paddedLine = line + " ".repeat(Math.max(0, padding))
    lines.push(`${sideBorder} ${paddedLine} ${sideBorder}`)
  })

  // Empty line before bottom border
  if (content.length > 0) {
    lines.push(`${sideBorder}${" ".repeat(boxWidth - 2)}${sideBorder}`)
  }

  lines.push(bottomBorder)

  if (padding) lines.push("")

  return lines
}

/**
 * Creates a section with title and content
 */
export function createSection(
  title: string,
  content: string[],
  subtitle?: string
) {
  const lines = []

  lines.push("")
  lines.push(title)

  if (subtitle) {
    lines.push(colors.gray(subtitle))
  }

  lines.push("")

  content.forEach((line) => {
    lines.push(line)
  })

  return lines
}

/**
 * Creates a welcome banner
 */
export function createWelcomeBanner(title: string, subtitle?: string) {
  const content = subtitle ? [subtitle] : []
  return createBox(`${title}`, content, { borderColor: "gray" })
}

/**
 * Creates an info box
 */
export function createInfoBox(title: string, content: string[]) {
  return createBox(`ℹ️  ${title}`, content, { borderColor: "cyan" })
}

/**
 * Creates a warning box
 */
export function createWarningBox(title: string, content: string[]) {
  return createBox(`⚠️  ${title}`, content, { borderColor: "yellow" })
}

/**
 * Creates a success box
 */
export function createSuccessBox(title: string, content: string[]) {
  return createBox(`✅ ${title}`, content, { borderColor: "green" })
}

/**
 * Logs multiple lines with proper spacing
 */
export function logLines(lines: string[]) {
  lines.forEach((line) => logger.log(line))
}

/**
 * Strips ANSI color codes from a string to get actual length
 */
function stripAnsi(str: string): string {
  // eslint-disable-next-line no-control-regex
  return str.replace(/\x1b\[[0-9;]*m/g, "")
}

/**
 * Creates a numbered list with proper spacing
 */
export function createNumberedList(
  items: Array<{ label: string; description?: string }>
) {
  const lines: string[] = []

  items.forEach((item, index) => {
    const number = colors.gray(`${index + 1}.`)
    lines.push(`  ${number} ${item.label}`)

    if (item.description) {
      lines.push(`     ${colors.gray(item.description)}`)
    }

    // Add spacing between items except the last one
    if (index < items.length - 1) {
      lines.push("")
    }
  })

  return lines
}

/**
 * Creates a preview box with code content
 */
export function createPreviewBox(title: string, content: string[]) {
  const lines = []

  lines.push("")
  lines.push(title)
  lines.push("")

  // Create bordered preview area with terminal width constraint
  const terminalWidth = getTerminalWidth()
  const maxLength = Math.max(...content.map((line) => stripAnsi(line).length))
  const boxWidth = Math.min(Math.max(50, maxLength + 4), terminalWidth - 4)

  const topBorder = "┌" + "─".repeat(boxWidth - 2) + "┐"
  const bottomBorder = "└" + "─".repeat(boxWidth - 2) + "┘"

  lines.push(topBorder)

  content.forEach((line, index) => {
    const lineNumber = colors.gray(`${index + 1}`)
    const paddedLine = line.padEnd(boxWidth - 6)
    lines.push(`│ ${lineNumber} ${paddedLine} │`)
  })

  lines.push(bottomBorder)
  lines.push("")

  return lines
}

/**
 * Creates an explanation section with bullet points
 */
export function createExplanationSection(title: string, points: string[]) {
  const lines = []

  lines.push("")
  lines.push(colors.cyan(title))
  lines.push("")

  points.forEach((point) => {
    lines.push(`  • ${point}`)
  })

  lines.push("")

  return lines
}

/**
 * Clears the terminal screen
 */
export function clearTerminal() {
  // Clear screen and move cursor to top-left
  process.stdout.write("\x1B[2J\x1B[0f")

  // Alternative method that works better on some terminals
  // console.clear()

  // For Windows compatibility
  if (process.platform === "win32") {
    process.stdout.write("\x1B[2J\x1B[3J\x1B[H")
  }
}

/**
 * Creates a subtle divider line that spans the terminal width
 */
export function createDivider() {
  const width = Math.min(getTerminalWidth() - 4, 80)
  return colors.gray("─".repeat(width))
}

/**
 * Creates a dash divider for prompts
 */
export function createSpacedDivider() {
  const width = Math.min(getTerminalWidth() - 4, 80)
  return colors.gray("-".repeat(width))
}
